import * as React from "react";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import * as Accordion from "@radix-ui/react-accordion";
import clsx from "clsx";
import { IconName, SVGIcon } from "@/components";
import { AccordionItem, SubItem } from "@/components/SidebarAccordion";
import { useMobileSidebar } from "../Sidebar/mobileSidebarContext";

/**
 * Typ dla sekcji grupującej elementy menu
 */
export interface SidebarSection {
  /** Unikalna wartość identyfikująca sekcję */
  id: string;
  /** Tytuł sekcji wyświetlany jako nagłówek */
  title: string;
  /** Elementy menu w danej sekcji */
  items: AccordionItem[];
}

// Funkcja płynnego przewijania do elementu
const smoothScrollToElement = (elementId: string, offset: number = 0) => {
  let frames = 0;

  const scroll = () => {
    // Gdy otwarte jest menu mobilne, body ma position: fixed (blokada scrolla tła)
    // i window.scrollTo nie zadziała. Poczekaj aż menu się zamknie i blokada zniknie.
    if (document.body.style.position === "fixed" && frames < 60) {
      frames += 1;
      requestAnimationFrame(scroll);
      return;
    }

    const element = document.getElementById(elementId);
    if (!element) return;

    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;

    window.scrollTo({
      top: elementPosition - offset,
      behavior: "smooth",
    });
  };

  scroll();
};

// Komponent dla pojedynczego SubItem - zmemoizowany
const SubItemComponent = React.memo<{
  subItem: SubItem;
  parentItem: AccordionItem;
  isActive: boolean;
  onSubItemClick: (subItem: SubItem, parentItem: AccordionItem) => void;
}>(({ subItem, parentItem, isActive, onSubItemClick }) => {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      onSubItemClick(subItem, parentItem);
    },
    [subItem, parentItem, onSubItemClick]
  );

  return (
    <div data-sidebar-id={subItem.elementId}>
      <a
        href="#"
        onClick={handleClick}
        className={clsx(
          "block pb-2 w-full rounded-xl transition-colors duration-200 group",
          "hover:text-primary-500",
          {
            "text-primary-500": isActive,
            "text-muted": !isActive,
          }
        )}
      >
        <div className="flex justify-between items-center w-full text-sm font-medium text-left">
          <div className="flex gap-3 items-center font-medium">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="xMidYMid meet"
              className={clsx("", {
                "stroke-primary": isActive,
                "stroke-text-muted group-hover:stroke-primary": !isActive,
              })}
            >
              <circle cx="7" cy="7" r="5.5" strokeWidth="3" />
            </svg>
            <span
              className={clsx("flex-1transition-colors duration-200", {
                "text-primary": isActive,
                "text-text-muted group-hover:text-primary": !isActive,
              })}
            >
              {subItem.label}
            </span>
          </div>
        </div>
      </a>
    </div>
  );
});

SubItemComponent.displayName = "SubItemComponent";

// Komponent dla pojedynczego Accordion Item - zmemoizowany
const AccordionItemComponent = React.memo<{
  item: AccordionItem;
  activeElementId: string | null;
  openItems: string[];
  onItemClick: (item: AccordionItem) => void;
  onSubItemClick: (subItem: SubItem, parentItem: AccordionItem) => void;
}>(({ item, activeElementId, openItems, onItemClick, onSubItemClick }) => {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      onItemClick(item);
    },
    [item, onItemClick]
  );

  // Sprawdź czy którykolwiek child jest aktywny
  const hasActiveChild = useMemo(() => {
    if (!item.items || !activeElementId) return false;
    return item.items.some((subItem) => subItem.elementId === activeElementId);
  }, [item.items, activeElementId]);

  // Rodzic jest aktywny jeśli on sam jest aktywny LUB którykolwiek z jego dzieci jest aktywny
  const isActive = item.elementId === activeElementId || hasActiveChild;
  const isOpen = openItems.includes(item.value);

  // Element bez dzieci - zwykły link
  if (!item.items || item.items.length === 0) {
    return (
      <a
        href="#"
        data-sidebar-id={item.elementId}
        onClick={handleClick}
        className={clsx(
          "block w-full rounded-xl transition-colors duration-200 group",
          "hover:bg-primary-500 hover:text-white",
          {
            "text-white bg-primary-500": isActive,
            "bg-transparent": !isActive,
          }
        )}
      >
        <div className="flex justify-between items-center w-full text-sm font-medium text-left">
          <div className="flex gap-5 items-center p-3 font-medium">
            <SVGIcon
              name={item.icon as IconName}
              fill={isActive ? "white" : undefined}
              className={clsx("transition-colors duration-200", {
                "fill-white": isActive,
                "fill-gray-500 group-hover:fill-white": !isActive,
              })}
            />
            <span
              className={clsx("transition-colors duration-200", {
                "text-white": isActive,
                "text-text-muted group-hover:text-white": !isActive,
              })}
            >
              {item.label}
            </span>
          </div>
        </div>
      </a>
    );
  }

  // Element z dziećmi - accordion
  return (
    <>
      <Accordion.Trigger
        data-sidebar-id={item.elementId}
        className={clsx(
          "block w-full rounded-xl transition-colors duration-200 group",
          "hover:bg-primary-500 hover:text-white",
          {
            "text-white bg-primary-500": isActive,
            "bg-transparent": !isActive,
          }
        )}
      >
        <div className="flex justify-between items-center w-full text-sm font-medium text-left">
          <div className="flex gap-5 items-center p-3 font-medium">
            <SVGIcon
              name={item.icon as IconName}
              fill={isActive ? "white" : undefined}
              className={clsx("", {
                "fill-white": isActive,
                "fill-gray-500 group-hover:fill-white": !isActive,
              })}
            />
            <span
              className={clsx("transition-colors duration-200", {
                "text-white": isActive,
                "text-text-muted group-hover:text-white": !isActive,
              })}
            >
              {item.label}
            </span>
          </div>
          <div
            className={clsx(
              "relative flex-shrink-0 p-2 mr-3 w-4 h-4 border-2 rounded-[4px]",
              {
                "border-blueLight": isActive,
                "border-gray-500 group-hover:border-blueLight": !isActive,
              }
            )}
          >
            <SVGIcon
              name="chevron-square"
              height={15}
              width={15}
              className={clsx(
                "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
                "transition-all duration-200 transform",
                {
                  "rotate-0": isOpen,
                  "rotate-[-90deg]": !isOpen,
                  "fill-blueLight": isActive,
                  "fill-gray-500 group-hover:fill-blueLight": !isActive,
                }
              )}
            />
          </div>
        </div>
      </Accordion.Trigger>
      <Accordion.Content className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden pt-3 text-sm">
        <div className="pr-2 pl-6 ml-6 border-l-2 border-grayBorder">
          {item.items?.map((subItem, index) => (
            <SubItemComponent
              key={subItem.elementId || index}
              subItem={subItem}
              parentItem={item}
              isActive={subItem.elementId === activeElementId}
              onSubItemClick={onSubItemClick}
            />
          ))}
        </div>
      </Accordion.Content>
    </>
  );
});

AccordionItemComponent.displayName = "AccordionItemComponent";

export interface SidebarAccordionProps {
  /**
   * @deprecated Użyj `sections` zamiast tego. Ten prop będzie usunięty w przyszłej wersji.
   * Lista elementów menu bez podziału na sekcje.
   */
  items?: AccordionItem[];
  /**
   * Lista sekcji zawierających elementy menu.
   * Każda sekcja ma tytuł i własną listę elementów.
   * Jeśli podano `sections`, prop `items` jest ignorowany.
   */
  sections?: SidebarSection[];
  onItemClick?: (item: AccordionItem) => void;
  onSubItemClick?: (subItem: SubItem, parentItem: AccordionItem) => void;
  scrollOffset?: number; // Offset w pikselach dla przewijania (domyślnie 0)
  onActiveItemChange?: (activeElementId: string | null) => void; // Callback wywoływany gdy zmienia się aktywna sekcja
  enableScrollTracking?: boolean; // Czy włączyć śledzenie przewijania (domyślnie true)
}

export const SidebarAccordion: React.FC<SidebarAccordionProps> = ({
  items: deprecatedItems,
  sections,
  onItemClick,
  onSubItemClick,
  scrollOffset = 0,
  onActiveItemChange,
  enableScrollTracking = true,
}) => {
  // Zamknięcie menu mobilnego (no-op na desktopie) - zdejmuje blokadę scrolla tła
  const { closeMobileMenu } = useMobileSidebar();

  // Normalizacja danych - konwertuj stary format `items` na `sections` dla spójnej obsługi
  const normalizedSections = useMemo<SidebarSection[]>(() => {
    if (sections && sections.length > 0) {
      return sections;
    }
    // Fallback do deprecated items - traktuj jako pojedynczą sekcję bez tytułu
    if (deprecatedItems && deprecatedItems.length > 0) {
      return [{ id: "__default__", title: "", items: deprecatedItems }];
    }
    return [];
  }, [sections, deprecatedItems]);

  // Spłaszczona lista wszystkich elementów ze wszystkich sekcji
  const allItems = useMemo(() => {
    return normalizedSections.flatMap((section) => section.items);
  }, [normalizedSections]);

  const [openItems, setOpenItems] = useState<string[]>(
    allItems.filter((item) => item.defaultOpen).map((item) => item.value)
  );

  // Poprawiona inicjalizacja dla NextJS SSR - bez użycia window w useState
  const [activeElementId, setActiveElementId] = useState<string | null>(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Referencja do kontenera sidebara - dla auto-scrollowania menu
  const sidebarContainerRef = useRef<HTMLDivElement>(null);

  // Referencje do sekcji dla Intersection Observer
  const sectionRefs = useRef<Map<string, Element>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollY = useRef<number>(0);
  const scrollDirection = useRef<"down" | "up">("down");
  const lastActiveElementId = useRef<string | null>(null);

  // Memoizuj wszystkie elementId z allItems
  const allElementIds = useMemo(() => {
    const ids: string[] = [];
    allItems.forEach((item: AccordionItem) => {
      if (item.elementId) {
        ids.push(item.elementId);
      }
      if (item.items) {
        item.items.forEach((subItem: SubItem) => {
          if (subItem.elementId) {
            ids.push(subItem.elementId);
          }
        });
      }
    });
    return ids;
  }, [allItems]);

  // Funkcja do aktualizacji URL z aktualnym elementId
  const updateUrlHash = useCallback(
    (elementId: string | null) => {
      if (elementId && !isUserScrolling) {
        const newUrl = elementId ? `#${elementId}` : "";
        if (window.location.hash !== newUrl) {
          window.history.replaceState(null, "", newUrl);
        }
      }
    },
    [isUserScrolling]
  );

  // Callback dla Intersection Observer - prostsza logika z kierunkiem scrolla
  const handleIntersection = useCallback(
    () => {
      if (!enableScrollTracking || isUserScrolling) return;

      // Wyczyść poprzedni timeout debounce
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Dodaj debounce aby uniknąć zbyt częstych zmian i migania na granicach
      debounceTimeoutRef.current = setTimeout(() => {
        // Wykryj kierunek scrollowania
        const currentScrollY = window.pageYOffset || window.scrollY;
        const isScrollingDown = currentScrollY > lastScrollY.current;
        lastScrollY.current = currentScrollY;
        scrollDirection.current = isScrollingDown ? "down" : "up";

        // WAŻNE: Sprawdź WSZYSTKIE sekcje w DOM, nie tylko te z entries
        // Intersection Observer entries mogą nie zawierać wszystkich widocznych sekcji
        const visibleSections: Array<{
          id: string;
          rect: DOMRect;
        }> = [];

        // Iteruj przez wszystkie zarejestrowane elementId
        allElementIds.forEach((elementId) => {
          const element = document.getElementById(elementId);
          if (element) {
            const rect = element.getBoundingClientRect();
            const viewportHeight = window.innerHeight;

            // Sprawdź czy sekcja jest widoczna w viewport
            // (przynajmniej częściowo)
            if (rect.bottom > 0 && rect.top < viewportHeight) {
              visibleSections.push({
                id: elementId,
                rect: rect,
              });
            }
          }
        });

        if (visibleSections.length === 0) return;

        // Znajdź aktywną sekcję na podstawie kierunku scrollowania
        let newActiveSection: string | null = null;
        const viewportHeight = window.innerHeight;

        // Najpierw sprawdź które sekcje spełniają warunki widoczności
        const candidateSections: string[] = [];

        // HISTEREZA: Jeśli już mamy aktywną sekcję, potrzebujemy trochę "bardziej" widocznej
        // nowej sekcji żeby przełączyć (zapobiega miganiu na granicach)
        const hysteresisMargin = 50; // 50px dodatkowego marginesu dla aktualnie aktywnej sekcji

        if (isScrollingDown) {
          // Scrollując w dół: sekcja jest aktywna gdy 40% jest widoczne od DOŁU
          // Czyli gdy bottom sekcji przekroczy 60% wysokości ekranu (licząc od góry)
          let threshold = viewportHeight * 0.6; // 60% od góry = 40% od dołu

          // Jeśli aktualna sekcja jest w visibleSections, dodaj histerezę
          const currentSectionVisible = visibleSections.some(
            (s) => s.id === lastActiveElementId.current
          );
          if (currentSectionVisible && lastActiveElementId.current) {
            threshold += hysteresisMargin;
          }

          for (const section of visibleSections) {
            // Sprawdź czy bottom sekcji jest powyżej progu (czyli 40% widoczne od dołu)
            if (section.rect.bottom >= threshold) {
              candidateSections.push(section.id);
            }
          }
        } else {
          // Scrollując w górę: sekcja jest aktywna gdy 40% jest widoczne od GÓRY
          // Czyli gdy top sekcji jest poniżej 40% wysokości ekranu
          let threshold = viewportHeight * 0.4; // 40% od góry

          // Jeśli aktualna sekcja jest w visibleSections, dodaj histerezę
          const currentSectionVisible = visibleSections.some(
            (s) => s.id === lastActiveElementId.current
          );
          if (currentSectionVisible && lastActiveElementId.current) {
            threshold -= hysteresisMargin;
          }

          // Iteruj od końca (sekcje niżej na stronie)
          for (let i = visibleSections.length - 1; i >= 0; i--) {
            const section = visibleSections[i];
            // Sprawdź czy top sekcji jest powyżej progu (czyli 40% widoczne od góry)
            if (section.rect.top <= threshold) {
              candidateSections.push(section.id);
            }
          }
        }

        // Jeśli mamy kandydatów, wybierz najbardziej szczegółowy (priorytet dla SubItems)
        if (candidateSections.length > 0) {
          // Sprawdź czy którykolwiek z kandydatów jest SubItem (zagnieżdżonym elementem)
          // SubItems to elementy które są w items[] jakiegoś AccordionItem
          const subItemCandidates = candidateSections.filter((candidateId) => {
            // Sprawdź czy ten candidateId jest SubItem
            return allItems.some((item: AccordionItem) =>
              item.items?.some((subItem: SubItem) => subItem.elementId === candidateId)
            );
          });

          // Jeśli są SubItems które spełniają warunki, wybierz pierwszy/ostatni (zależnie od kierunku)
          if (subItemCandidates.length > 0) {
            newActiveSection = isScrollingDown
              ? subItemCandidates[0]
              : subItemCandidates[subItemCandidates.length - 1];
          } else {
            // W przeciwnym razie wybierz pierwszy/ostatni z kandydatów
            newActiveSection = isScrollingDown
              ? candidateSections[0]
              : candidateSections[candidateSections.length - 1];
          }
        }

        // Jeśli nie znaleziono sekcji spełniającej warunki, użyj pierwszej widocznej
        if (!newActiveSection && visibleSections.length > 0) {
          newActiveSection = visibleSections[0].id;
        }

        // Przełącz aktywny element tylko jeśli się zmienił
        if (newActiveSection && newActiveSection !== activeElementId) {
          lastActiveElementId.current = newActiveSection;
          setActiveElementId(newActiveSection);
          updateUrlHash(newActiveSection);
          onActiveItemChange?.(newActiveSection);
        }
      }, 150); // Debounce 150ms - zwiększone z 100ms aby zmniejszyć miganie
    },
    [
      activeElementId,
      allElementIds,
      allItems,
      enableScrollTracking,
      isUserScrolling,
      updateUrlHash,
      onActiveItemChange,
    ]
  );

  // Inicjalizacja Intersection Observer
  useEffect(() => {
    if (!enableScrollTracking || allElementIds.length === 0) return;

    // Uproszczona konfiguracja - logika kierunku jest w callback
    const options: IntersectionObserverInit = {
      root: null,
      // Brak rootMargin - sprawdzamy cały viewport
      rootMargin: "0px",
      // Sprawdzamy czy sekcja jest w ogóle widoczna
      threshold: [0, 0.1, 0.5, 1.0],
    };

    // Wrapper który wywołuje handleIntersection bez parametrów
    // (parametr entries nie jest już używany, sprawdzamy wszystkie sekcje ręcznie)
    const observerCallback = () => {
      handleIntersection();
    };

    // Utwórz nowy observer
    const observer = new IntersectionObserver(observerCallback, options);
    observerRef.current = observer;

    // Obserwuj wszystkie sekcje
    const currentSections = sectionRefs.current;
    allElementIds.forEach((elementId) => {
      const element = document.getElementById(elementId);
      if (element && observer) {
        currentSections.set(elementId, element);
        observer.observe(element);
      }
    });

    // Cleanup
    return () => {
      if (observer) {
        observer.disconnect();
      }
      currentSections.clear();
      // Wyczyść debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [allElementIds, enableScrollTracking, scrollOffset, handleIntersection]);

  // Wspólna logika scrollowania do sekcji
  const scrollToSection = useCallback(
    (elementId: string) => {
      // Włącz flagę user scrolling aby wyłączyć automatyczne śledzenie
      setIsUserScrolling(true);
      setActiveElementId(elementId);
      updateUrlHash(elementId);
      // Zamknij menu mobilne - zdejmuje blokadę scrolla tła (position: fixed),
      // dzięki czemu smoothScrollToElement może przewinąć stronę do sekcji.
      closeMobileMenu();
      smoothScrollToElement(elementId, scrollOffset);
      onActiveItemChange?.(elementId);

      // Wyczyść poprzedni timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Wyłącz flagę scrolling po zakończeniu animacji
      // Zwiększony timeout dla pewności że animacja się zakończyła
      scrollTimeoutRef.current = setTimeout(() => {
        setIsUserScrolling(false);
      }, 1000);
    },
    [scrollOffset, updateUrlHash, onActiveItemChange, closeMobileMenu]
  );

  const handleValueChange = useCallback((value: string[]) => {
    setOpenItems(value);
  }, []);

  const handleItemClick = useCallback(
    (item: AccordionItem) => {
      // Jeśli element ma elementId, przewiń do niego
      if (item.elementId) {
        scrollToSection(item.elementId);
      }
      // Wywołaj callback jeśli istnieje
      onItemClick?.(item);
    },
    [scrollToSection, onItemClick]
  );

  const handleSubItemClick = useCallback(
    (subItem: SubItem, parentItem: AccordionItem) => {
      // Jeśli podelement ma elementId, przewiń do niego
      if (subItem.elementId) {
        scrollToSection(subItem.elementId);
      }
      // Wywołaj callback jeśli istnieje
      onSubItemClick?.(subItem, parentItem);
    },
    [scrollToSection, onSubItemClick]
  );

  // Inicjalizacja activeElementId z URL hash (tylko po stronie klienta)
  useEffect(() => {
    if (!isInitialized && typeof window !== "undefined") {
      const hash = window.location.hash.substring(1);
      if (hash) {
        setActiveElementId(hash);
      }
      setIsInitialized(true);
    }
  }, [isInitialized]);

  // Rozwiń grupę po wejściu w sekcję lub dowolną z jej podsekcji.
  useEffect(() => {
    if (!activeElementId) return;

    // Znajdź rodzica dla aktywnego elementu
    allItems.forEach((item: AccordionItem) => {
      if (item.items?.length) {
        const hasActiveSubItem = item.items.some(
          (subItem: SubItem) => subItem.elementId === activeElementId
        );
        if (item.elementId === activeElementId || hasActiveSubItem) {
          setOpenItems((prev) => {
            // Dodaj tylko jeśli nie jest już otwarty
            if (!prev.includes(item.value)) {
              return [...prev, item.value];
            }
            return prev;
          });
        }
      }
    });
  }, [activeElementId, allItems]);

  // Przewiń do sekcji z URL hash przy inicjalizacji
  useEffect(() => {
    if (!isInitialized) return;

    const initialHash =
      typeof window !== "undefined" && window.location.hash
        ? window.location.hash.substring(1)
        : null;

    if (initialHash) {
      const element = document.getElementById(initialHash);
      if (element) {
        // Zwiększony timeout aby zapewnić pełne załadowanie DOM w NextJS
        // Szczególnie ważne gdy są duże obrazy lub komponenty
        setTimeout(() => {
          smoothScrollToElement(initialHash, scrollOffset);
        }, 500);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]); // Uruchom po inicjalizacji

  // Auto-scroll sidebara do aktywnego elementu gdy jest poza widocznym obszarem
  useEffect(() => {
    if (!activeElementId || isUserScrolling || !sidebarContainerRef.current) return;

    const SCROLL_MARGIN = 200;

    // Daj czas na otwarcie accordionu (animacja) zanim przewiniemy
    const timeoutId = setTimeout(() => {
      if (!sidebarContainerRef.current) return;

      const activeItem = sidebarContainerRef.current.querySelector(
        `[data-sidebar-id="${activeElementId}"]`
      );
      if (!activeItem) return;

      // Znajdź scrollowalny kontener (rodzic z overflow-y: auto/scroll)
      const scrollContainer = sidebarContainerRef.current.closest(
        "[class*='overflow-y']"
      ) as HTMLElement | null;
      if (!scrollContainer) return;

      const containerRect = scrollContainer.getBoundingClientRect();
      const itemRect = (activeItem as HTMLElement).getBoundingClientRect();

      // Sprawdź czy element jest poza widocznym obszarem (z marginesem)
      if (itemRect.top < containerRect.top + SCROLL_MARGIN) {
        // Element jest powyżej widocznego obszaru
        scrollContainer.scrollBy({
          top: itemRect.top - containerRect.top - SCROLL_MARGIN,
          behavior: "smooth",
        });
      } else if (itemRect.bottom > containerRect.bottom - SCROLL_MARGIN) {
        // Element jest poniżej widocznego obszaru
        scrollContainer.scrollBy({
          top: itemRect.bottom - containerRect.bottom + SCROLL_MARGIN,
          behavior: "smooth",
        });
      }
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [activeElementId, isUserScrolling]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Accordion.Root
      ref={sidebarContainerRef}
      type="multiple"
      className="px-2 space-y-2"
      value={openItems}
      onValueChange={handleValueChange}
    >
      {normalizedSections.map((section) => (
        <div key={section.id}>
          {/* Nagłówek sekcji - renderowany tylko gdy title nie jest pusty */}
          {section.title && (
            <div className="relative px-3 pt-4 pb-2 my-4 text-xs font-semibold tracking-wider text-gray-400 uppercase">
              <div className="text-sm font-medium text-left uppercase text-[#95a4b2]">{section.title}</div>
              <div className="w-2 h-11 absolute left-[-10px] top-0 rounded-tr-[15px] rounded-br-[15px] bg-[#95a4b2]" />
            </div>
          )}
          {/* Elementy menu w sekcji */}
          {section.items.map((item) => (
            <Accordion.Item
              key={item.value}
              value={item.value}
              className="overflow-hidden"
            >
              <AccordionItemComponent
                item={item}
                activeElementId={activeElementId}
                openItems={openItems}
                onItemClick={handleItemClick}
                onSubItemClick={handleSubItemClick}
              />
            </Accordion.Item>
          ))}
        </div>
      ))}
    </Accordion.Root>
  );
};

export default SidebarAccordion;
